
import { Draggable } from "@hello-pangea/dnd";
import { Edit2, Trash2, GripVertical } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { BookingStatus } from "@/hooks/useEventManagement";

interface BookingStatusItemProps {
  status: BookingStatus;
  index: number;
  onEdit: (statusId: string) => void;
  onDelete: (statusId: string) => void;
  reordering: boolean;
}

export function BookingStatusItem({ 
  status, 
  index, 
  onEdit, 
  onDelete, 
  reordering 
}: BookingStatusItemProps) {
  return (
    <Draggable 
      key={status.id} 
      draggableId={status.id} 
      index={index} 
      isDragDisabled={reordering}
    >
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={`flex items-center justify-between p-4 border rounded-lg bg-card transition-all ${
            snapshot.isDragging ? 'shadow-lg ring-2 ring-primary/20' : ''
          } ${reordering ? 'opacity-50' : ''}`}
        >
          <div className="flex items-center gap-4">
            <div 
              {...provided.dragHandleProps} 
              className={reordering ? 'cursor-not-allowed' : 'cursor-grab'}
            >
              <GripVertical className="h-4 w-4 text-muted-foreground" />
            </div>
            
            <div className="flex items-center gap-3">
              <div 
                className="w-4 h-4 rounded-full border"
                style={{ backgroundColor: status.color }}
              />
              <div>
                <div className="font-medium">{status.display_name}</div>
                <div className="text-sm text-muted-foreground font-mono">
                  {status.status_key}
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Badge variant="outline">{status.category}</Badge>
            <span className="text-sm text-muted-foreground">
              Order: {status.sort_order}
            </span>
            <Badge variant={status.is_active ? "default" : "secondary"}>
              {status.is_active ? "Active" : "Inactive"}
            </Badge>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" disabled={reordering}>
                  <Edit2 className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onEdit(status.id)}>
                  <Edit2 className="h-4 w-4 mr-2" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => onDelete(status.id)}
                  className="text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      )}
    </Draggable>
  );
}
